/**
 * OptiVehicle Profile data — ONE source for Create Ticket and the Ticket
 * Command Center.
 *
 * Reads ONLY OptiBay records, through RLS: the canonical `vehicles` row (basic
 * identity — the compatibility projection), the current
 * `vehicle_identity_facts` rows (with provenance), and the vehicle's shop
 * history resolved from existing records by vehicle_id (tickets + their jobs
 * and odometer, service recommendations), the previous system's repair orders
 * LINKED to this vehicle by exact VIN (imported — external records, labelled as
 * such), and the odometer revision trail; mileage is derived from those
 * (components/vehicle-profile/mileage.ts). It never calls NHTSA: viewing a
 * vehicle is not a reason to decode it. Decoding / editing go through the
 * trusted `vehicle-profile` Edge function (useVehicleProfileActions).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  resolveProfile, summarize, type ResolvedFact, type StoredFact, type VehicleBasics,
} from '../../supabase/functions/_shared/vehicleProfile/facts';
import {
  mileageObservations, type ImportedReading, type MileageObservation, type OdometerRevision,
} from '@/components/vehicle-profile/mileage';

export interface VehicleHistoryVisit {
  ticketId: string;
  number: string;
  status: string;
  createdAt: string;
  completedAt: string | null;
  odometerMiles: number | null;
  jobs: string[];
}
export interface VehicleRecommendation {
  title: string;
  status: string;
  recommendedAt: string;
  resolvedAt: string | null;
}
/** A repair order from the shop's previous system, linked to this vehicle by exact VIN. */
export interface ImportedRepairOrder extends ImportedReading {
  status: string | null;
  sourceFormat: string;
  jobs: Array<{ line: number; description: string; isComeback: boolean; isWarranty: boolean }>;
}
export interface VehicleProfileData {
  vehicleId: string;
  basics: (VehicleBasics & { has_vin: boolean }) | null;
  facts: StoredFact[];
  resolved: ResolvedFact[];
  summary: { title: string; specLine: string };
  history: VehicleHistoryVisit[];
  recommendations: VehicleRecommendation[];
  hasDecodedFacts: boolean;
  /** Imported repair orders linked by exact VIN (newest first). */
  imported: ImportedRepairOrder[];
  /** The imported history could not be read — say so; never imply there is none. */
  importedUnavailable: boolean;
  /** Every odometer reading, newest observation first, with questionable ones flagged. */
  mileage: MileageObservation[];
  /** Other ACTIVE vehicles of the shop sharing this verified VIN (duplicates awaiting review). */
  duplicateCount: number;
  /** Set when the requested id was a merged (retired) duplicate and this is its survivor. */
  mergedFromVehicleId?: string;
}

const db = supabase as unknown as { from: (t: string) => any };

export const vehicleProfileKey = (vehicleId: string | null | undefined) => ['optivehicle-profile', vehicleId ?? null] as const;

/**
 * A merged (retired) duplicate redirects to the vehicle it was merged into: any
 * reference still holding the retired id opens the canonical profile, which says
 * so. Followed at most a few hops (a merge chain), never in a loop.
 */
async function loadProfile(requestedId: string): Promise<VehicleProfileData> {
  let vehicleId = requestedId;
  for (let hop = 0; hop < 4; hop++) {
    const { data, error } = await db.from('vehicles').select('merged_into_vehicle_id').eq('id', vehicleId).maybeSingle();
    if (error || !data?.merged_into_vehicle_id || data.merged_into_vehicle_id === vehicleId) break;
    vehicleId = data.merged_into_vehicle_id as string;
  }
  const profile = await loadProfileFor(vehicleId);
  return vehicleId === requestedId ? profile : { ...profile, mergedFromVehicleId: requestedId };
}

async function loadProfileFor(vehicleId: string): Promise<VehicleProfileData> {
  const [basicsRes, factsRes, ticketsRes, recsRes] = await Promise.all([
    db.from('vehicles').select('id, shop_id, year, make, model, engine_size, gvwr_lbs, vin_status, vin_bi_v2').eq('id', vehicleId).maybeSingle(),
    db.from('vehicle_identity_facts').select('fact_key, value, source_class, source, confidence, recorded_at')
      .eq('vehicle_id', vehicleId).is('superseded_at', null),
    db.from('tickets').select('id, simple_ticket_number, ticket_number, status, created_at, completed_at, odometer_miles, ticket_jobs(deleted_at, jobs(name))')
      .eq('vehicle_id', vehicleId).is('deleted_at', null).order('created_at', { ascending: false }).limit(50),
    db.from('service_recommendations').select('title, status, recommended_at, resolved_at')
      .eq('vehicle_id', vehicleId).order('recommended_at', { ascending: false }).limit(50),
  ]);
  if (basicsRes.error) throw basicsRes.error;
  if (factsRes.error) throw factsRes.error;
  const b = basicsRes.data;
  const basics = b ? { year: b.year, make: b.make, model: b.model, engine_size: b.engine_size, gvwr_lbs: b.gvwr_lbs, has_vin: b.vin_status != null } : null;
  const facts = (factsRes.data ?? []) as StoredFact[];
  const resolved = resolveProfile(facts, basics);
  const history: VehicleHistoryVisit[] = (ticketsRes.data ?? []).map((t: any) => {
    const num = t.simple_ticket_number ?? t.ticket_number;
    return {
      ticketId: t.id,
      number: num ? `#${String(num).padStart(4, '0')}` : `#${String(t.id).slice(0, 8)}`,
      status: t.status,
      createdAt: t.created_at,
      completedAt: t.completed_at ?? null,
      odometerMiles: t.odometer_miles ?? null,
      jobs: (t.ticket_jobs ?? []).filter((j: any) => !j.deleted_at).map((j: any) => j.jobs?.name).filter(Boolean),
    };
  });
  const recommendations: VehicleRecommendation[] = (recsRes.data ?? []).map((r: any) => ({
    title: r.title, status: r.status, recommendedAt: r.recommended_at, resolvedAt: r.resolved_at ?? null,
  }));
  const { imported, importedUnavailable } = await loadImportedHistory(vehicleId);
  const revisions = await loadOdometerRevisions(history.map((h) => h.ticketId));
  // Another ACTIVE vehicle of this shop with the same verified VIN = a duplicate to review.
  let duplicateCount = 0;
  if (b?.vin_status === 'verified' && b.vin_bi_v2) {
    const dup = await db.from('vehicles').select('id', { count: 'exact', head: true })
      .eq('shop_id', b.shop_id).eq('vin_bi_v2', b.vin_bi_v2).is('merged_into_vehicle_id', null).neq('id', vehicleId);
    duplicateCount = dup.error ? 0 : (dup.count ?? 0);
  }
  return {
    vehicleId, basics, facts, resolved, summary: summarize(resolved), history, recommendations,
    hasDecodedFacts: facts.some((f) => f.source_class === 'decoded'),
    imported, importedUnavailable,
    mileage: mileageObservations(history, imported, revisions),
    duplicateCount,
  };
}

/**
 * The previous system's repair orders for this vehicle: only those whose imported
 * vehicle is LINKED to it (exact VIN, same shop — link_shop_history_vehicles). RLS
 * limits every read to the viewer's shop. A failed read fails OPEN (the profile
 * still loads) but is reported as unavailable, never as "no history".
 */
async function loadImportedHistory(vehicleId: string): Promise<{ imported: ImportedRepairOrder[]; importedUnavailable: boolean }> {
  try {
    const hv = await db.from('shop_history_vehicles').select('id').eq('live_vehicle_id', vehicleId).is('deleted_at', null);
    if (hv.error) return { imported: [], importedUnavailable: true };
    const ids = ((hv.data ?? []) as Array<{ id: string }>).map((r) => r.id);
    if (ids.length === 0) return { imported: [], importedUnavailable: false };
    const ros = await db.from('shop_history_repair_orders')
      .select('id, ro_number, opened_at, closed_at, source_status, odometer_in, odometer_out, source_format')
      .in('history_vehicle_id', ids).is('deleted_at', null).order('opened_at', { ascending: false }).limit(50);
    if (ros.error) return { imported: [], importedUnavailable: true };
    const roRows = (ros.data ?? []) as any[];
    const roIds = roRows.map((r) => r.id as string);
    const jobsByRo = new Map<string, ImportedRepairOrder['jobs']>();
    if (roIds.length > 0) {
      const jobs = await db.from('shop_history_jobs')
        .select('history_repair_order_id, line_number, description, is_comeback, is_warranty')
        .in('history_repair_order_id', roIds).is('deleted_at', null).order('line_number', { ascending: true });
      if (jobs.error) return { imported: [], importedUnavailable: true };
      for (const j of (jobs.data ?? []) as any[]) {
        if (!j.description) continue;
        const list = jobsByRo.get(j.history_repair_order_id) ?? [];
        list.push({ line: j.line_number, description: j.description, isComeback: !!j.is_comeback, isWarranty: !!j.is_warranty });
        jobsByRo.set(j.history_repair_order_id, list);
      }
    }
    return {
      importedUnavailable: false,
      imported: roRows.map((r) => ({
        id: r.id, roNumber: r.ro_number ?? null, openedAt: r.opened_at ?? null, closedAt: r.closed_at ?? null,
        odometerIn: r.odometer_in ?? null, odometerOut: r.odometer_out ?? null,
        status: r.source_status ?? null, sourceFormat: r.source_format, jobs: jobsByRo.get(r.id) ?? [],
      })),
    };
  } catch {
    return { imported: [], importedUnavailable: true };
  }
}

/** The append-only correction trail for these visits' odometer readings (fail-open: no markers). */
async function loadOdometerRevisions(ticketIds: string[]): Promise<OdometerRevision[]> {
  if (ticketIds.length === 0) return [];
  try {
    const { data, error } = await db.from('ticket_odometer_revisions')
      .select('ticket_id, previous_miles, new_miles, changed_at').in('ticket_id', ticketIds);
    if (error) return [];
    return ((data ?? []) as any[]).map((r) => ({ ticketId: r.ticket_id, previousMiles: r.previous_miles, newMiles: r.new_miles, changedAt: r.changed_at }));
  } catch {
    return [];
  }
}

/** Resolve the vehicle for a ticket (Command Center) without any external call. */
export function useTicketVehicleId(ticketId: string | null | undefined, known: string | null | undefined) {
  return useQuery({
    queryKey: ['optivehicle-ticket-vehicle', ticketId ?? null],
    enabled: !known && !!ticketId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await db.from('tickets').select('vehicle_id').eq('id', ticketId).maybeSingle();
      if (error) throw error;
      return (data?.vehicle_id as string | null) ?? null;
    },
  });
}

export function useVehicleProfile(vehicleId: string | null | undefined) {
  return useQuery({
    queryKey: vehicleProfileKey(vehicleId),
    enabled: !!vehicleId,
    staleTime: 60_000,
    queryFn: () => loadProfile(vehicleId as string),
  });
}

export function useVehicleProfileActions(shopId: string, vehicleId: string | null | undefined) {
  const qc = useQueryClient();
  const call = async (body: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke('vehicle-profile', { body: { shop_id: shopId, vehicle_id: vehicleId, ...body } });
    if (error) throw new Error(error.message || 'Vehicle profile update failed');
    if (data?.error) throw new Error(String(data.error));
    return data;
  };
  const done = () => qc.invalidateQueries({ queryKey: vehicleProfileKey(vehicleId) });
  return {
    decode: useMutation({ mutationFn: () => call({ action: 'decode' }), onSuccess: done }),
    assert: useMutation({ mutationFn: (v: { factKey: string; value: string }) => call({ action: 'assert', fact_key: v.factKey, value: v.value }), onSuccess: done }),
    retract: useMutation({ mutationFn: (factKey: string) => call({ action: 'retract', fact_key: factKey }), onSuccess: done }),
  };
}

/**
 * Concise returning-vehicle summary: "Last visit: Sep 14, 2026 • 81,204 mi" / "5 prior visits".
 * The mileage shown is THAT visit's own reading (or none) — it used to borrow an
 * older visit's reading and pair it with the newest date. The latest known mileage
 * across all records is shown separately, with its own date and source.
 */
export function historySummary(history: readonly VehicleHistoryVisit[], excludeTicketId?: string | null) {
  const prior = history.filter((h) => h.ticketId !== excludeTicketId);
  if (!prior.length) return null;
  const last = prior[0];
  return { count: prior.length, lastDate: last.completedAt ?? last.createdAt, lastOdometer: last.odometerMiles ?? null };
}
